import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const switchCardVariants = cva(
  cn(
    "h-auto w-full relative flex text-wrap items-start border outline-none transition-all",
    "has-data-[state=checked]:border-primary/80 has-data-[state=checked]:ring-2 has-data-[state=checked]:ring-primary/10",
    "[&>svg]:opacity-60",
  ),
  {
    variants: {
      variant: {
        // In a Frame the card is one of its panels: the panel's border and
        // shadow (like RadioCard); the checked ring stays outside the border.
        outline:
          "border-input shadow-xs hover:bg-sidebar hover:dark:bg-muted in-data-[slot=frame]:border-border in-data-[slot=frame]:shadow-xs/5 in-data-[slot=frame]:radius-full:shadow-soft",
        secondary: "border-transparent shadow-none",
        ghost: "border-transparent shadow-none has-data-[state=checked]:ring-0 has-data-[state=checked]:border-transparent",
      },
      size: {
        xs: "p-2.5 gap-1 rounded-md [&>svg]:size-3.5",
        sm: "p-3 gap-1.5 rounded-lg [&>svg]:size-4",
        default: "p-4 gap-2 rounded-lg [&>svg]:size-4",
        lg: "p-5 gap-3 rounded-xl [&>svg]:size-5",
      },
    },
    defaultVariants: {
      variant: "outline",
      size: "default",
    },
  },
);

type SwitchCardSize = NonNullable<VariantProps<typeof switchCardVariants>["size"]>;

// The card's size and disabled state reach its parts (switch, label,
// description, content gap) through this context, so both are set once on
// the card. Every part still accepts its own className/size to override.
const SwitchCardContext = React.createContext<{ size: SwitchCardSize; disabled: boolean }>({
  size: "default",
  disabled: false,
});

const SWITCH_SIZE: Record<SwitchCardSize, React.ComponentProps<typeof Switch>["size"]> = {
  xs: "xs",
  sm: "sm",
  default: "default",
  lg: "default",
};

const CONTENT_GAP: Record<SwitchCardSize, string> = {
  xs: "gap-0.25",
  sm: "gap-0.5",
  default: "gap-2",
  lg: "gap-2",
};

const LABEL_SIZE: Record<SwitchCardSize, string> = {
  xs: "text-xs",
  sm: "text-sm",
  default: "text-sm",
  lg: "text-base",
};

const DESCRIPTION_SIZE: Record<SwitchCardSize, string> = {
  xs: "text-xs",
  sm: "text-xs",
  default: "text-xs",
  lg: "text-sm",
};

export type SwitchCardProps = React.ComponentProps<"div"> &
  VariantProps<typeof switchCardVariants> & {
    /** Disables the switch inside and dims the card. */
    disabled?: boolean;
  };

/**
 * A div with a button's look, not a button: the switch inside is the control
 * (its ::after covers the card, so a click anywhere toggles it), and a button
 * around it made a button inside a button.
 *
 * @example
 * import { useState } from "react"
 * import {
 *   SwitchCard,
 *   SwitchCardContent,
 *   SwitchCardDescription,
 *   SwitchCardLabel,
 *   SwitchCardSwitch,
 * } from "@/components/ui/switch-card"
 *
 * export function AutopilotSetting() {
 *   const [on, setOn] = useState(true)
 *   return (
 *     <SwitchCard>
 *       <SwitchCardSwitch id="autopilot" checked={on} onCheckedChange={setOn} aria-describedby="autopilot-description" />
 *       <SwitchCardContent>
 *         <SwitchCardLabel htmlFor="autopilot">Autopilot</SwitchCardLabel>
 *         <SwitchCardDescription id="autopilot-description">Amy sends and answers emails on her own.</SwitchCardDescription>
 *       </SwitchCardContent>
 *     </SwitchCard>
 *   )
 * }
 */
export function SwitchCard({
  className,
  variant,
  size,
  disabled = false,
  ...props
}: SwitchCardProps): React.ReactElement {
  const resolvedSize: SwitchCardSize = size ?? "default";
  return (
    <SwitchCardContext.Provider value={{ size: resolvedSize, disabled }}>
      <Button
        asChild
        variant={variant ?? "outline"}
        scope={!disabled}
        className={cn(
          switchCardVariants({ variant, size }),
          "data-disabled:pointer-events-none data-disabled:opacity-50",
          className,
        )}
      >
        <div
          data-slot="switch-card"
          data-size={resolvedSize}
          data-disabled={disabled || undefined}
          {...props}
        />
      </Button>
    </SwitchCardContext.Provider>
  );
}

export function SwitchCardSwitch({
  className,
  size,
  disabled,
  ...props
}: React.ComponentProps<typeof Switch>): React.ReactElement {
  const card = React.useContext(SwitchCardContext);
  return (
    <Switch
      size={size ?? SWITCH_SIZE[card.size]}
      disabled={disabled ?? card.disabled}
      className={cn("order-1 after:absolute after:inset-0", className)}
      data-slot="switch-card-switch"
      {...props}
    />
  );
}

export function SwitchCardContent({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  const cardSize = React.useContext(SwitchCardContext).size;
  return (
    <div
      className={cn("grid grow", CONTENT_GAP[cardSize], className)}
      data-slot="switch-card-content"
      {...props}
    />
  );
}

export function SwitchCardLabel({
  className,
  ...props
}: React.ComponentProps<typeof Label>): React.ReactElement {
  const cardSize = React.useContext(SwitchCardContext).size;
  return (
    <Label
      className={cn(LABEL_SIZE[cardSize], "[&>svg]:opacity-60", className)}
      data-slot="switch-card-label wrap-break-word text-wrap"
      {...props}
    />
  );
}

export function SwitchCardDescription({
  className,
  ...props
}: React.ComponentProps<"p">): React.ReactElement {
  const cardSize = React.useContext(SwitchCardContext).size;
  return (
    <p
      className={cn(
        "text-muted-foreground text-start wrap-break-word text-wrap font-normal",
        DESCRIPTION_SIZE[cardSize],
        className,
      )}
      data-slot="switch-card-description"
      {...props}
    />
  );
}
